import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../app/StyleGuide';
import WhiteLabel from '../filters/WhiteLabel';
import { CurriculumImage } from './CurriculumImage';
import Overlay from './Overlay';
import SectionHeader from './SectionHeader';
import LoadingState from './LoadingState';
import ErrorMessage from './ErrorMessage';

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  max-width: 835px;
  border-radius: ${Border.radius};
`;

const UpperContainer = styled.div`
  padding: ${Grid._6};
`;

const Header = styled.div`
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${Grid._1};
  right: 0;
  background: none;
  border: 0;
  padding: 0;
  text-decoration-line: underline;
  color: ${Color.Blue._50};
`;

const SubscriptionsList = styled.div`
  display: flex;
  gap: ${Grid._5};
  margin-top: ${Grid._3};
`;

const SubscriptionContainer = styled.div<{ $orientation: 'horizontal' | 'vertical' }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Grid._3};
  align-items: center;

  ${({ $orientation }) =>
    $orientation === 'horizontal' &&
    `
    @media screen and (min-width: ${Breakpoints.screen_xs}) {
      grid-template-columns: min-content 1fr;
      gap: ${Grid._4};
    }
  `}
`;

const LogoImage = styled.img`
  width: min(100%, 230px);
  height: 80px;
  border-radius: ${Border.radius};
  padding: ${Grid._4};
  background: ${Color.Gray._10};
`;

const SubscriptionDetails = styled.div`
  display: grid;
  gap: ${Grid._4};

  p {
    margin: 0;
    color: ${Type.Color.medium};
  }
`;

const Name = styled.div`
  color: ${Type.Color.medium};
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.normal};
  margin: 0;
`;
const Subtitle = styled.div`
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.bold};
`;

const InstructionContainer = styled.div`
  background: ${Color.Gray._10};
  padding-bottom: ${Grid._6};
`;

const Instruction = styled.div`
  padding: ${Grid._5} ${Grid._6} ${Grid._4} ${Grid._6};
  border-top: 1px solid ${Color.Gray._20};
`;

const InstructionTitle = styled.div`
  font-weight: ${Type.Weight.bold};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    text-align: right;
  }
`;

type Subscription = {
  name: string;
  subtitle: string;
  brandCode?: string;
  additionalDetail?: string;
};

type Instruction = {
  title: string;
  content: React.ReactNode;
};

type SubscriptionOverlayProps = {
  title: string;
  heading: string;
  subscriptions: Subscription[];
  instructions: Instruction[];
  dismissHandler: () => void;
  subscriptionOrientation?: 'horizontal' | 'vertical';
  isLoading?: boolean;
  isError?: boolean;
};

export const SubscriptionOverlay = ({
  title,
  heading,
  subscriptions,
  instructions,
  dismissHandler,
  subscriptionOrientation = 'horizontal',
  isLoading = false,
  isError = false,
}: SubscriptionOverlayProps) => (
  <Overlay dismissHandler={dismissHandler}>
    {isLoading ? (
      <Container>
        <LoadingState />
      </Container>
    ) : isError ? (
      <Container>
        <ErrorMessage />
      </Container>
    ) : (
      <Container data-qa-hook="newCurriculumModal">
        <UpperContainer>
          <Header>
            <SectionHeader title={title} />
            <CloseButton data-qa-hook="newCurriculumModalClose" onClick={() => dismissHandler()}>
              Close
            </CloseButton>
          </Header>
          <h5>{heading}</h5>

          {!!subscriptions.length && (
            <SubscriptionsList>
              {subscriptions.map(({ name, subtitle, brandCode, additionalDetail }) => (
                <SubscriptionContainer key={`${name}-${subtitle}`} $orientation={subscriptionOrientation}>
                  {brandCode ? (
                    <CurriculumImage brandCode={brandCode} />
                  ) : (
                    <LogoImage alt={WhiteLabel.name} src={WhiteLabel.logoSmSrc || WhiteLabel.logoSrc} />
                  )}
                  <SubscriptionDetails>
                    <div>
                      <Name>{name}</Name>
                      <Subtitle>{subtitle}</Subtitle>
                    </div>
                    {additionalDetail && <p>{additionalDetail}</p>}
                  </SubscriptionDetails>
                </SubscriptionContainer>
              ))}
            </SubscriptionsList>
          )}
        </UpperContainer>
        {!!instructions.length && (
          <InstructionContainer>
            {instructions.map(({ title, content }) => (
              <Instruction key={title} className="grid-container grid-sm-col-4 grid-col-gap-40 grid-row-gap-8">
                <InstructionTitle className="grid-sm-col-span-1">{title}</InstructionTitle>
                <div className="grid-sm-col-span-3">{content}</div>
              </Instruction>
            ))}
          </InstructionContainer>
        )}
      </Container>
    )}
  </Overlay>
);
